import { useState, useEffect } from 'react';
import apiService from '../../../../services/apiService';

/**
 * Leaderboards, on the public site.
 *
 * These already existed for the results desk, behind a staff login — which is
 * no use to the several thousand people actually playing in the league. Same
 * figures, no account needed.
 */
const ThrowballLeaderboards = () => {
  const [sports, setSports] = useState([]);
  const [sport, setSport] = useState(null);
  const [city, setCity] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('standings');
  const [category, setCategory] = useState('');
  const [standings, setStandings] = useState(null);

  useEffect(() => {
    apiService.get('/public/leaderboards/sports')
      .then((res) => {
        const list = res.data?.data || [];
        setSports(list);
        // Open on whatever has been played most — never on an empty board.
        const best = [...list].sort((a, b) => b.played - a.played)[0];
        setSport(best?.sport || null);
      })
      .catch(() => setSports([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!sport) return;
    setData(null);
    apiService
      .get('/public/leaderboards', { params: { sport, city: city || undefined } })
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [sport, city]);

  useEffect(() => {
    if (!sport) return;
    setStandings(null);
    apiService
      .get('/public/standings', {
        params: { sport, city: city || undefined, category: category || undefined },
      })
      .then((res) => setStandings(res.data))
      .catch(() => setStandings(null));
  }, [sport, city, category]);

  const chip = (on) => ({
    padding: '7px 15px',
    borderRadius: 20,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: on ? 700 : 500,
    border: `1px solid ${on ? 'var(--tb-primary-color)' : '#d9dee5'}`,
    background: on ? 'var(--tb-primary-color)' : '#fff',
    color: on ? '#fff' : '#42505f',
  });

  const boards = Object.entries(data?.boards || {});

  return (
    <section className="tb-page-section">
      <div className="tb-container">
        <div className="tb-heading-top">
          <h1 className="tb-page-title">Leaderboards</h1>
          <span className="tb-mini-line"></span>
        </div>

        {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

        {!loading && sports.length === 0 && (
          <p style={{ color: '#6b7280' }}>No matches have been played yet.</p>
        )}

        {sports.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 10, margin: '18px 0 16px' }}>
              {[['standings', 'Standings'], ['records', 'Records']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setTab(k)} style={{
                  padding: '9px 22px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  border: `1px solid ${tab === k ? 'var(--tb-primary-color)' : '#d9dee5'}`,
                  background: tab === k ? 'var(--tb-primary-color)' : '#fff',
                  color: tab === k ? '#fff' : '#42505f',
                }}>{label}</button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
              {sports.map((s) => (
                <button key={s.sport} type="button" style={chip(sport === s.sport)}
                        onClick={() => { setSport(s.sport); setCity(''); setCategory(''); }}>
                  {s.label}
                </button>
              ))}
            </div>

            {(data?.filters?.cities || []).length > 1 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                <button type="button" style={chip(!city)} onClick={() => setCity('')}>All cities</button>
                {data.filters.cities.map((c) => (
                  <button key={c} type="button" style={chip(city === c)} onClick={() => setCity(c)}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'standings' && (
          <>
            {(standings?.filters?.categories || []).length > 1 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                <button type="button" style={chip(!category)} onClick={() => setCategory('')}>
                  All draws
                </button>
                {standings.filters.categories.map((c) => (
                  <button key={c} type="button" style={chip(category === c)}
                          onClick={() => setCategory(c)}>{c}</button>
                ))}
              </div>
            )}

            {!standings && <p style={{ color: '#6b7280' }}>Loading…</p>}

            {standings && standings.count === 0 && (
              <p style={{ color: '#6b7280' }}>
                No table yet — those matches have not been played.
              </p>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
              gap: 18,
            }}>
              {(standings?.tables || []).map((t, i) => <Table key={i} table={t} />)}
            </div>
          </>
        )}

        {tab === 'records' && (
          <>
            {data && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: 20 }}>
                {data.overview.played.toLocaleString('en-IN')} matches played
                {city ? ` in ${city}` : ' across the league'}
              </p>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 16,
            }}>
              {boards.map(([key, board]) => <Board key={key} board={board} />)}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

/** A group's league table, or how far each entrant went in a knockout draw. */
const Table = ({ table }) => {
  const group = table.kind === 'group';
  const th = {
    textAlign: 'right', padding: '7px 6px', fontSize: '0.68rem', fontWeight: 800,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9aa3af',
    borderBottom: '2px solid #eef1f5', whiteSpace: 'nowrap',
  };
  const td = { textAlign: 'right', padding: '8px 6px', fontSize: '0.86rem', color: '#111827' };

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px',
    }}>
      <div style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#111827' }}>
          {table.city} · {table.category}
          {group ? ` · Group ${table.group}` : ''}
        </h3>
        <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: '#9aa3af' }}>
          {group ? 'Three points a win, one a draw' : 'Ranked by the round reached'}
        </p>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: group ? 380 : 300 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', width: 26 }}>#</th>
              <th style={{ ...th, textAlign: 'left' }}>Team</th>
              <th style={th}>P</th>
              <th style={th}>W</th>
              {group && <th style={th}>D</th>}
              <th style={th}>L</th>
              {group && <th style={th}>GF</th>}
              {group && <th style={th}>GA</th>}
              {group && <th style={th}>GD</th>}
              {group ? <th style={th}>Pts</th> : <th style={{ ...th, textAlign: 'left' }}>Reached</th>}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr key={r.team + r.position} style={{
                borderBottom: '1px solid #f4f6f8',
                // Top of a group goes through, so it is marked as such.
                background: group && r.position === 1 ? '#f6f3ff' : 'transparent',
              }}>
                <td style={{ ...td, textAlign: 'left', color: '#9aa3af', fontWeight: 800 }}>
                  {r.position}
                </td>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.team}</td>
                <td style={td}>{r.played}</td>
                <td style={td}>{r.won}</td>
                {group && <td style={td}>{r.drawn}</td>}
                <td style={td}>{r.lost}</td>
                {group && <td style={td}>{r.for}</td>}
                {group && <td style={td}>{r.against}</td>}
                {group && <td style={td}>{r.difference > 0 ? `+${r.difference}` : r.difference}</td>}
                {group
                  ? <td style={{ ...td, fontWeight: 800 }}>{r.points}</td>
                  : <td style={{ ...td, textAlign: 'left', color: '#6b7280' }}>{r.reached || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Board = ({ board }) => {
  const rows = board.rows || [];
  const top = rows[0]?.value || 1;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '16px 18px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 12, gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#111827' }}>{board.title}</h3>
        <span style={{
          fontSize: '0.66rem', letterSpacing: '0.09em', textTransform: 'uppercase',
          color: '#9aa3af', fontWeight: 800,
        }}>{board.unit}</span>
      </div>

      {rows.length === 0 && <p style={{ color: '#9aa3af', fontSize: '0.85rem' }}>Nothing to rank yet.</p>}

      {rows.map((r, i) => (
        <div key={`${r.name}-${i}`} style={{
          display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10,
          alignItems: 'center', padding: '7px 0',
          borderTop: i === 0 ? 'none' : '1px solid #f1f3f6',
        }}>
          <span style={{
            fontSize: '0.78rem', fontWeight: 800,
            color: i < 3 ? 'var(--tb-primary-color)' : '#9aa3af',
          }}>{i + 1}</span>

          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '0.88rem', fontWeight: 600, color: '#111827',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{r.name}</div>
            {r.detail && (
              <div style={{ fontSize: '0.72rem', color: '#9aa3af', marginTop: 1 }}>{r.detail}</div>
            )}
            <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: '#eef1f5' }}>
              <div style={{
                width: `${Math.round((r.value / top) * 100)}%`, height: '100%', borderRadius: 2,
                background: i < 3 ? 'var(--tb-primary-color)' : '#c7d0da',
              }} />
            </div>
          </div>

          <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#111827' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
};

export default ThrowballLeaderboards;
